<script setup lang="ts">
import { useFairSheetSwipe } from '@/ui/primitives/FairDialog/useFairSheetSwipe'
import { computed, onBeforeUnmount, onMounted, ref, useSlots } from 'vue'
import Drawer from 'primevue/drawer'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { useFairOverlay } from '@/ui/primitives/FairOverlay'

/**
 * Drawer — DS "Large overlays" › Drawer (289:6763).
 * Size=Default (289:5399): right panel 440px, label/caps title, hairlines around content.
 * Size=Mobile (289:6882): bottom sheet with grabber, centred body/strong title.
 * `size="auto"` follows the 1024px desktop breakpoint.
 */
interface FairDrawerProps {
  visible?: boolean
  title?: string
  size?: 'auto' | 'default' | 'mobile'
  /** Figma "Left button" — back arrow before the title */
  showBack?: boolean
  closable?: boolean
  closeLabel?: string
  backLabel?: string
  /** Desktop panel width in px (DS default 440; Profile · Style DNA is 520). */
  width?: number
  /** Desktop edge the panel slides from (Home menu is on the left). Mobile is always the bottom. */
  side?: 'left' | 'right'
}

const props = withDefaults(defineProps<FairDrawerProps>(), {
  visible: false,
  title: '',
  size: 'auto',
  showBack: false,
  closable: true,
  closeLabel: 'Close',
  backLabel: 'Back',
  width: 440,
  side: 'right',
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
  back: []
}>()

const slots = useSlots()

useFairOverlay(() => props.visible)

const compactQuery = ref<MediaQueryList | null>(null)
const isCompactViewport = ref(false)

function syncViewport() {
  isCompactViewport.value = compactQuery.value?.matches ?? false
}

onMounted(() => {
  compactQuery.value = window.matchMedia('(max-width: 1023px)')
  syncViewport()
  compactQuery.value.addEventListener('change', syncViewport)
})

onBeforeUnmount(() => {
  compactQuery.value?.removeEventListener('change', syncViewport)
})

const isMobile = computed(() =>
  props.size === 'mobile' || (props.size === 'auto' && isCompactViewport.value),
)

function setVisible(value: boolean) {
  emit('update:visible', value)
}

function handleClose() {
  setVisible(false)
  emit('close')
}

/** Mobile sheet: pull the grabber / header down to close. */
const swipe = useFairSheetSwipe({
  enabled: () => isMobile.value && props.closable,
  onDismiss: handleClose,
})
</script>

<template>
  <Drawer
    :visible="visible"
    :position="isMobile ? 'bottom' : side"
    :modal="true"
    :dismissable="true"
    :block-scroll="false"
    :show-close-icon="false"
    :pt="{
      root: {
        class: ['fair-drawer', isMobile ? 'fair-drawer--mobile' : 'fair-drawer--default'],
        style: { '--fair-drawer-width': `${width}px` },
      },
      mask: { class: 'fair-drawer-mask' },
    }"
    @update:visible="setVisible"
    @hide="emit('close')"
  >
    <template #container>
      <div
        class="fair-drawer__panel"
        :style="swipe.style.value"
      >
        <div v-if="isMobile" class="fair-drawer__grabber" aria-hidden="true" v-on="swipe.handlers" />

        <header
          class="fair-drawer__header"
          v-on="isMobile ? swipe.handlers : {}"
        >
          <FairIconButton
            v-if="showBack"
            icon="arrow-left"
            :aria-label="backLabel"
            @click="emit('back')"
          />
          <span v-else-if="isMobile" class="fair-drawer__header-spacer" aria-hidden="true" />
          <h2 class="fair-drawer__title">
            <slot name="title">{{ title }}</slot>
          </h2>
          <FairIconButton
            v-if="closable"
            icon="close"
            :aria-label="closeLabel"
            @click="handleClose"
          />
          <span v-else-if="isMobile" class="fair-drawer__header-spacer" aria-hidden="true" />
        </header>

        <div v-if="!isMobile" class="fair-drawer__divider" aria-hidden="true" />

        <div class="fair-drawer__content">
          <slot />
        </div>

        <template v-if="slots.footer">
          <div v-if="!isMobile" class="fair-drawer__divider" aria-hidden="true" />
          <footer class="fair-drawer__footer">
            <slot name="footer" />
          </footer>
        </template>
      </div>
    </template>
  </Drawer>
</template>
